<template>
  <div class="public-order-page">
    <LoadingBar v-if="loading" message="Loading purchase order..." />

    <div v-else class="container-xl py-4 py-lg-5">
      <div class="mb-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div>
            <div class="text-uppercase small fw-semibold text-secondary mb-2">Vendor Order View</div>
            <h2 class="mb-1 fw-bold text-dark">{{ order.name || order.external_id || 'Purchase Order' }}</h2>
            <div class="text-secondary small">
              <span class="me-3"><i class="fa fa-hashtag me-1"></i>{{ order.external_id || 'N/A' }}</span>
              <span><i class="fa fa-calendar me-1"></i>{{ formatDate(order.created_at) }}</span>
            </div>
          </div>
          <PoStatus :status="order.status" />
        </div>
      </div>

      <div class="row g-4">
        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 bg-white">
            <div class="card-body p-4">
              <div class="d-flex align-items-center gap-2 mb-3">
                <i class="fa fa-info-circle text-primary"></i>
                <h5 class="mb-0 fw-bold text-dark">PO Details</h5>
              </div>
              <div class="row g-3">
                <div class="col-md-6 col-lg-3">
                  <div class="text-secondary small mb-1">PO Number</div>
                  <div class="fw-semibold text-dark">{{ order.external_id || 'N/A' }}</div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="text-secondary small mb-1">Order Name</div>
                  <div class="fw-semibold text-dark">{{ order.name || 'N/A' }}</div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="text-secondary small mb-1">Expected Delivery</div>
                  <div class="fw-semibold text-dark">{{ formatDate(order.expected_delivery_date) }}</div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="text-secondary small mb-1">Total Amount</div>
                  <div class="fw-semibold text-success">{{ formatCurrency(order.total_price) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 bg-white">
            <div class="card-body p-4">
              <div class="d-flex align-items-center gap-2 mb-3">
                <i class="fa fa-building text-primary"></i>
                <h5 class="mb-0 fw-bold text-dark">Vendor Detail- {{ vendorName }}</h5>
              </div>
             

              <div class="table-responsive border rounded-3">
                <table class="table table-sm table-borderless align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th class="py-2 ps-3">Name</th>
                      <th class="py-2">Email</th>
                      <th class="py-2">Role</th>
                      <th class="py-2 pe-3">Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(contact, idx) in contactList" :key="idx">
                      <td class="ps-3 py-2  text-dark">{{ contact.vendor_contact?.name || contact.name || 'N/A' }}</td>
                      <td class="py-2">
                        <a v-if="contact.vendor_contact?.email || contact.email" :href="`mailto:${contact.vendor_contact?.email || contact.email}`" class="text-decoration-none text-primary">
                          {{ contact.vendor_contact?.email || contact.email || 'N/A' }}
                        </a>
                        <span v-else class="text-secondary">N/A</span>
                      </td>
                      <td class="py-2">
                        <span class="badge bg-primary-subtle text-primary rounded-pill px-2 py-1">{{ contact.vendor_contact?.role || contact.role || 'N/A' }}</span>
                      </td>
                      <td class="pe-3 py-2">
                        <a v-if="contact.vendor_contact?.phone || contact.phone" :href="`tel:${contact.vendor_contact?.phone || contact.phone}`" class="text-decoration-none text-dark">
                          {{ contact.vendor_contact?.phone || contact.phone || 'N/A' }}
                        </a>
                        <span v-else class="text-secondary">N/A</span>
                      </td>
                    </tr>
                    <tr v-if="!contactList.length">
                      <td colspan="4" class="text-center text-secondary py-4">No contacts available for this vendor.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
            <div class="card-header bg-white border-bottom px-4 py-3">
              <div class="d-flex align-items-center justify-content-between gap-3">
                <div class="d-flex align-items-center gap-2">
                  <i class="fa fa-list text-primary"></i>
                  <h5 class="mb-0 fw-bold text-dark">PO Items</h5>
                </div>
                <span class="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">{{ (order.items || []).length }} items</span>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="custom-order-table table-sm align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Unit Rate</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in order.items || []" :key="item.id || index">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <div class="fw-semibold text-dark">{{ getItemName(item) }}</div>
                        <div class="small text-secondary">{{ getItemCode(item) || 'No code provided' }}</div>
                      </td>
                      <td>{{ item.quantity }} {{ item.unit || 'pcs' }}</td>
                      <td>{{ formatCurrency(item.unit_price) }}</td>
                      <td>{{ formatCurrency(item.total_price) }}</td>
                    </tr>
                    <tr v-if="!(order.items || []).length">
                      <td colspan="5" class="text-center text-secondary py-4">No items available for this order.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="d-flex justify-content-end align-items-center px-4 py-3 border-top bg-white">
                <div class="fw-bold text-dark fs-5">Total Amount: <span class="text-success">{{ formatCurrency(order.total_price) }}</span></div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="card border-0 shadow-sm rounded-4 bg-white">
            <div class="card-body p-4">
              <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
                <div class="d-flex align-items-center gap-2">
                  <i class="fa fa-paperclip text-primary"></i>
                  <h5 class="mb-0 fw-bold text-dark">Attachments</h5>
                </div>
                <span class="badge bg-secondary-subtle text-secondary rounded-pill px-3 py-2">{{ imageList.length + fileList.length }} files</span>
              </div>

              <div v-if="imageList.length || fileList.length" class="row g-3">
                <div v-for="(image, index) in imageList" :key="'img-' + (image.id || index)" class="col-6 col-sm-4 col-md-3">
                  <div class="border rounded-3 overflow-hidden bg-light cursor-pointer" @click="previewMedia(image, 'image')">
                    <div class="ratio ratio-16x9 bg-white border-bottom d-flex align-items-center justify-content-center">
                      <img :src="resolveFileUrl(image.file_path)" :alt="image.file_name || 'Image'" class="w-100 h-100 object-fit-cover" />
                    </div>
                    <div class="p-2">
                      <div class="small fw-semibold text-dark text-truncate">{{ image.file_name || `Image ${index + 1}` }}</div>
                      <div class="small text-secondary">{{ formatFileSize(image.file_size) }}</div>
                    </div>
                  </div>
                </div>

                <div v-for="(file, index) in fileList" :key="'file-' + (file.id || index)" class="col-6 col-sm-4 col-md-3">
                  <div class="border rounded-3 overflow-hidden bg-light cursor-pointer" @click="previewMedia(file, 'file')">
                    <div class="ratio ratio-16x9 bg-white border-bottom d-flex align-items-center justify-content-center">
                      <i :class="getFileIcon(file.file_name || file.file_type)" class="fa-2x text-secondary"></i>
                    </div>
                    <div class="p-2">
                      <div class="small fw-semibold text-dark text-truncate">{{ file.file_name || `File ${index + 1}` }}</div>
                      <div class="small text-secondary">{{ formatFileSize(file.file_size) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="text-center text-secondary py-5 border rounded-3 bg-light">
                <i class="fa fa-folder-open-o fa-2x mb-2"></i>
                <p class="mb-0">No attachments uploaded for this order.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal fade" id="poMediaModal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content border-0 overflow-hidden rounded-4">
            <div class="modal-header border-bottom">
              <h6 class="modal-title fw-semibold text-dark">File Preview</h6>
              <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body p-0 bg-dark d-flex justify-content-center align-items-center" style="min-height: 350px;">
              <template v-if="previewType === 'image'">
                <img :src="previewUrl" class="img-fluid object-fit-contain w-100" style="max-height: 75vh;" alt="Preview" />
              </template>
              <template v-else-if="previewType === 'file'">
                <iframe :src="previewUrl" frameborder="0" class="w-100" style="height: 70vh;"></iframe>
              </template>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import PoStatus from '@/components/PoStatus.vue'

export default {
  name: 'PublicOrderView',
  components: { LoadingBar, PoStatus },
  data() {
    return {
      order: {},
      loading: false,
      external_id: this.$route.params.external_id,
      previewUrl: '',
      previewType: ''
    }
  },
  computed: {
    vendorName() {
      const vendor = this.order.vendor || {}
      return [vendor.first_name, vendor.last_name].filter(Boolean).join(' ') || vendor.external_id || 'N/A'
    },
    vendorEmail() {
      return this.order.vendor?.email || this.order.vendor_contact?.email || ''
    },
    vendorPhone() {
      return this.order.vendor?.phone || this.order.vendor_contact?.phone || ''
    },
    imageList() {
      return this.order.images || []
    },
    fileList() {
      return this.order.files || []
    },
    contactList() {
      return this.order.purchase_order_vendor_contacts || []
    }
  },
  mounted() {
    this.fetchOrder()
  },
  methods: {
    getFullName(user) {
      if (!user) return 'N/A'
      if (typeof user === 'string' || typeof user === 'number') return String(user)
      return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.external_id || 'N/A'
    },
    getItemName(item) {
      const material = item.get_material || item.itemable || {}
      return material.name || material.code || material.external_id || `Item #${item.itemable_id || item.id}`
    },
    getItemCode(item) {
      const material = item.get_material || item.itemable || {}
      return material.code || material.external_id || ''
    },
    formatDate(value) {
      if (!value) return 'N/A'
      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === '') return '$0.00'
      return `$${Number(amount).toFixed(2)}`
    },
    formatFileSize(bytes) {
      if (!bytes) return 'Unknown'
      const kb = bytes / 1024
      return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`
    },
    getFileIcon(fileName) {
      if (!fileName) return 'fa fa-file'
      const ext = String(fileName).split('.').pop().toLowerCase()
      const map = {
        pdf: 'fa fa-file-pdf-o',
        doc: 'fa fa-file-word-o', docx: 'fa fa-file-word-o',
        xls: 'fa fa-file-excel-o', xlsx: 'fa fa-file-excel-o', csv: 'fa fa-file-excel-o',
        jpg: 'fa fa-file-image-o', jpeg: 'fa fa-file-image-o', png: 'fa fa-file-image-o',
        gif: 'fa fa-file-image-o', webp: 'fa fa-file-image-o',
        txt: 'fa fa-file-text-o', md: 'fa fa-file-text-o',
        zip: 'fa fa-file-archive-o', rar: 'fa fa-file-archive-o'
      }
      return map[ext] || 'fa fa-file-o'
    },
    resolveFileUrl(path) {
      return resolveAssetFileUrl(path)
    },
    previewMedia(file, type) {
      const fileUrl = this.resolveFileUrl(file.file_path)
      if (type === 'image') {
        this.previewType = 'image'
        this.previewUrl = fileUrl
      } else {
        const ext = this.getFileExtension(file.file_name || file.file_path)
        const officeFormats = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
        if (officeFormats.includes(ext)) {
          this.previewType = 'file'
          this.previewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
        } else if (ext === 'pdf') {
          this.previewType = 'file'
          this.previewUrl = fileUrl
        } else {
          window.open(fileUrl, '_blank')
          return
        }
      }
      const el = document.getElementById('poMediaModal')
      if (el) {
        const Modal = window.bootstrap?.Modal || window.Modal
        if (Modal) new Modal(el).show()
      }
    },
    getFileExtension(name = '') {
      return String(name).split('.').pop().toLowerCase()
    },
    async fetchOrder() {
      this.loading = true
      try {
        const result = await form_api_submit({
          url: `purchase-order/profile/${this.external_id}`,
          method: 'GET',
          data: {},
          show_success_toast: false,
          show_error_toast: true
        })
        if (result.success) this.order = result.data || {}
      } catch (e) {
        console.error('Error fetching order:', e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.public-order-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
}

.cursor-pointer {
  cursor: pointer;
}

.rounded-4 {
  border-radius: 1rem;
}

.card {
  background-color: #ffffff !important;
}

.card-header {
  background-color: #ffffff !important;
}

.object-fit-cover {
  object-fit: cover;
}

.custom-order-table {
  width: 100% !important;
  background: #ffffff !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

.custom-order-table thead th {
  background: #f8fafc !important;
  color: #475569 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  font-weight: 600 !important;
  padding: 0.9rem 1rem !important;
}

.custom-order-table tbody td {
  background: #ffffff !important;
  color: inherit !important;
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 0.9rem 1rem !important;
}

.custom-order-table tbody tr:last-child td {
  border-bottom: none !important;
}
</style>
